import {
    WAVE_SPAWN_DISTANCE,
    PACK_COST,
    LOT_OPEN_COST,
    QUALITY_TIER_THRESHOLDS,
    TYPE_SPECIFIC_LOT
} from '../config/balance.js';

/**
 * WaveStateManager handles all wave/pack state operations
 *
 * Provides a focused API for wave pack management while keeping
 * actual state in GameState.state.waves for save compatibility.
 */
export class WaveStateManager {
    /**
     * @param {GameState} gameState - Reference to main game state
     */
    constructor(gameState) {
        this.gameState = gameState;
    }

    /**
     * Get wave tier name from quality value
     * @param {number} quality - Quality value (0.0 to 1.0)
     * @returns {string} - Tier name
     */
    getWaveTier(quality) {
        if (quality >= QUALITY_TIER_THRESHOLDS.vectorized) return 'vectorized';
        if (quality >= QUALITY_TIER_THRESHOLDS.golden) return 'golden';
        if (quality >= QUALITY_TIER_THRESHOLDS.pristine) return 'pristine';
        if (quality >= QUALITY_TIER_THRESHOLDS.standard) return 'standard';
        return 'faded';
    }

    /**
     * Get the waves state object (shorthand)
     */
    get waves() {
        return this.gameState.state.waves;
    }

    // ===== Pack Management =====

    /**
     * Add a pack to available packs
     * @param {object} packData - Pack data with milestone, cost, packQuality
     */
    addAvailablePack(packData) {
        // Safeguard: prevent duplicate packs at the same milestone
        const existingPack = this.waves.availablePacks.find(p => p && p.milestone === packData.milestone);
        if (existingPack) {
            console.warn('[WaveStateManager] Duplicate pack prevented at milestone:', packData.milestone);
            return;
        }

        this.waves.availablePacks.push(packData);
        this.gameState.save();
    }

    /**
     * Remove a pack by milestone number
     * @param {number} milestone - The milestone identifier
     * @returns {boolean} True if removed
     */
    removeAvailablePack(milestone) {
        // First, clean up any null entries in the array
        this.waves.availablePacks = this.waves.availablePacks.filter(p => p != null);

        const index = this.waves.availablePacks.findIndex(pack => pack && pack.milestone === milestone);
        if (index !== -1) {
            this.waves.availablePacks.splice(index, 1);
            this.gameState.save();
            return true;
        }
        return false;
    }

    /**
     * Get all available packs sorted by milestone
     * @returns {Array} Sorted array of pack objects
     */
    getAvailablePacks() {
        // Filter out any null entries that may have been introduced
        return this.waves.availablePacks.filter(p => p).sort((a, b) => a.milestone - b.milestone);
    }

    /**
     * Open a pack - charges miles on FIRST open, stores generated items, awards wavedust
     * Subsequent opens return the same stored items (no re-rolling)
     * @param {number} milestone - The milestone identifier
     * @param {Array} generatedItems - Items generated by PackGenerator (only used on first open)
     * @returns {object|false} The pack data with items and wavedust earned, or false if failed
     */
    openPack(milestone, generatedItems = null) {
        const pack = this.waves.availablePacks.find(pack => pack.milestone === milestone);
        if (!pack) {
            console.warn('Pack not found for milestone:', milestone);
            return false;
        }

        // If pack already opened, return it with stored items
        if (pack.opened) {
            return pack;
        }

        // First time opening - check if can afford (wavedust)
        if (pack.cost > 0 && this.gameState.getWavedust() < pack.cost) {
            console.warn('Cannot afford pack:', pack.cost, 'wavedust:', this.gameState.getWavedust());
            return false;
        }

        // Deduct wavedust cost on first open
        if (pack.cost > 0) {
            this.gameState.spendWavedust(pack.cost);
            console.log(`[WaveStateManager] Spent ${pack.cost} wavedust on lot`);
        }

        // Award bonus salvage for tutorial lot
        if (pack.isTutorial) {
            const tutorialBonus = 150;
            this.gameState.addWavedust(tutorialBonus);
            pack.tutorialBonus = tutorialBonus;
            console.log(`[WaveStateManager] Tutorial lot opened - awarded ${tutorialBonus} bonus salvage`);
        }

        // Mark as opened and store generated items
        pack.opened = true;
        pack.storedItems = generatedItems || [];

        this.gameState.save(true);
        return pack;
    }

    /**
     * Confirm pack purchase after user has selected an item
     * Removes pack from available (miles already deducted on open)
     * @param {number} milestone - The milestone identifier
     * @returns {boolean} True if successful
     */
    confirmPackPurchase(milestone) {
        const packIndex = this.waves.availablePacks.findIndex(pack => pack.milestone === milestone);
        if (packIndex === -1) {
            console.warn('Pack not found for milestone:', milestone);
            return false;
        }

        // Increment pack counter
        this.waves.packsPurchased++;

        // Remove pack from available packs array
        this.waves.availablePacks.splice(packIndex, 1);

        this.gameState.save(true);
        return true;
    }

    /**
     * @deprecated Use openPack() + confirmPackPurchase() instead
     * Purchase a pack by milestone (legacy method for backwards compatibility)
     * Deducts cost from miles and removes pack from available
     * @param {number} milestone - The milestone identifier
     * @returns {object|false} The purchased pack data, or false if failed
     */
    purchasePack(milestone) {
        const pack = this.openPack(milestone);
        if (!pack) return false;

        if (this.confirmPackPurchase(milestone)) {
            return pack;
        }
        return false;
    }

    /**
     * Set the next wave milestone
     * @param {number} milestone - The next milestone value
     */
    setNextMilestone(milestone) {
        this.waves.nextMilestone = milestone;
        this.gameState.save();
    }

    // ===== Seen State (UI Notification Tracking) =====

    /**
     * Check if a wave milestone has been seen by the user
     * @param {number} milestone - The wave milestone
     * @returns {boolean} True if seen
     */
    hasSeenWave(milestone) {
        return this.waves.seenWaveMilestones?.includes(milestone) || false;
    }

    /**
     * Get count of unseen waves
     * @returns {number} Number of unseen available waves
     */
    getUnseenWaveCount() {
        const seen = this.waves.seenWaveMilestones || [];
        return this.waves.availablePacks.filter(p => p && !seen.includes(p.milestone)).length;
    }

    /**
     * Mark all current waves as seen
     */
    markAllWavesSeen() {
        const milestones = this.waves.availablePacks.filter(p => p).map(p => p.milestone);
        this.waves.seenWaveMilestones = [...new Set([
            ...(this.waves.seenWaveMilestones || []),
            ...milestones
        ])];
        this.gameState.save();
    }

    /**
     * Clean up seen milestones that no longer have corresponding packs
     * (called when packs are purchased/removed)
     */
    cleanupSeenWaves() {
        const activeMilestones = this.waves.availablePacks.map(p => p.milestone);
        this.waves.seenWaveMilestones = (this.waves.seenWaveMilestones || [])
            .filter(m => activeMilestones.includes(m));
    }

    // ===== Dynamic Spawn System Helpers =====

    /**
     * Get the next wave spawn mile target
     */
    getNextWaveAtMile() {
        return this.waves.nextWaveAtMile;
    }

    /**
     * Set the next wave spawn mile target
     * @param {number} mile - Mile at which next wave spawns
     */
    setNextWaveAtMile(mile) {
        this.waves.nextWaveAtMile = mile;
    }

    /**
     * Get the next wave quality
     */
    getNextWaveQuality() {
        return this.waves.nextWaveQuality;
    }

    /**
     * Set the next wave quality
     * @param {number} quality - Quality value 0.0-1.0
     */
    setNextWaveQuality(quality) {
        this.waves.nextWaveQuality = quality;
    }

    /**
     * Get luck accumulator value
     */
    getLuckAccumulator() {
        return this.waves.luckAccumulator;
    }

    /**
     * Set luck accumulator value
     * @param {number} luck - Luck value -1 to 1
     */
    setLuckAccumulator(luck) {
        this.waves.luckAccumulator = luck;
    }

    /**
     * Get number of packs purchased
     */
    getPacksPurchased() {
        return this.waves.packsPurchased;
    }

    // ===== Offline Wave Spawns =====

    /**
     * Check for wave spawns that would have occurred during offline period
     * Generates waves and adds them to available packs
     * @param {number} startMiles - Total miles at start of offline period
     * @param {number} endMiles - Total miles at end of offline period
     * @returns {Array} Array of waves that spawned { atMile, quality, cost }
     */
    checkOfflineWaveSpawns(startMiles, endMiles) {
        const wavesFound = [];

        // Get current wave target
        let nextWaveAt = this.waves.nextWaveAtMile;
        let nextWaveQuality = this.waves.nextWaveQuality;

        // Track slotType from state or futureWaves
        let nextSlotType = this.waves.nextSlotType || null;

        // Check if we crossed the current wave milestone
        while (nextWaveAt <= endMiles && nextWaveAt > startMiles) {
            // Check if this is the tutorial wave (first wave at or before 25 miles)
            const isTutorialWave = nextWaveAt <= 25 && !this.gameState.state.waves.tutorialShown;

            // Calculate pack cost (tutorial wave is FREE)
            const cost = isTutorialWave ? 0 : this._calculatePackCost(nextWaveQuality, nextWaveAt);

            // Create the pack
            const pack = {
                milestone: nextWaveAt,
                totalMiles: nextWaveAt,
                cost: cost,
                packQuality: nextWaveQuality,
                isTutorial: isTutorialWave,
                slotType: isTutorialWave ? null : nextSlotType  // No slot type for tutorial
            };

            // Mark tutorial as collected (but not yet shown - will show on rejoin)
            if (isTutorialWave) {
                this.gameState.state.waves.tutorialCollectedOffline = true;
            }

            // Safeguard: check for duplicate before adding
            const existingPack = this.waves.availablePacks.find(p => p && p.milestone === nextWaveAt);
            if (existingPack) {
                console.warn('[WaveStateManager] Offline duplicate prevented at milestone:', nextWaveAt);
            } else {
                // Add to available packs
                this.waves.availablePacks.push(pack);
                wavesFound.push(pack);
            }

            // Advance to next wave from futureWaves
            if (this.waves.futureWaves && this.waves.futureWaves.length > 0) {
                const nextWave = this.waves.futureWaves.shift();
                nextWaveAt = nextWave.atMile;
                nextWaveQuality = nextWave.quality;
                nextSlotType = nextWave.slotType || null;

                // Update state
                this.waves.nextWaveAtMile = nextWaveAt;
                this.waves.nextWaveQuality = nextWaveQuality;
                this.waves.nextSlotType = nextSlotType;
                this.waves.nextMilestone = nextWaveAt;
                this.waves.lastSpawnDistance = nextWave.distance;
            } else {
                // No future waves pre-rolled, need to generate one
                // Use centralized balance config for offline generation
                const { MIN_DISTANCE, MAX_DISTANCE, BASE_QUALITY, QUALITY_PER_MILE, QUALITY_VARIANCE } = WAVE_SPAWN_DISTANCE;
                const distance = MIN_DISTANCE + Math.random() * (MAX_DISTANCE - MIN_DISTANCE);
                nextWaveAt = nextWaveAt + distance;
                nextWaveQuality = BASE_QUALITY + (distance - MIN_DISTANCE) * QUALITY_PER_MILE + (Math.random() * QUALITY_VARIANCE * 2 - QUALITY_VARIANCE);

                // Roll slotType for this newly generated wave (25% chance)
                nextSlotType = this._rollSlotType();

                // Apply Wave Luck bonus from driver skills
                const waveLuckBonus = this.gameState?.getWaveLuckBonus() || 0;
                if (waveLuckBonus > 0) {
                    nextWaveQuality *= (1 + waveLuckBonus);
                }

                nextWaveQuality = Math.max(0.04, Math.min(1.0, nextWaveQuality));

                this.waves.nextWaveAtMile = nextWaveAt;
                this.waves.nextWaveQuality = nextWaveQuality;
                this.waves.nextSlotType = nextSlotType;
                this.waves.nextMilestone = nextWaveAt;
            }
        }

        // Save immediately if any waves were found (force to bypass throttle)
        // This ensures waves are persisted before any race conditions can occur
        if (wavesFound.length > 0) {
            this.gameState.save(true);
        }

        return wavesFound;
    }

    /**
     * Calculate pack cost based on quality tier
     * @param {number} quality - Pack quality 0.0-1.0
     * @param {number} atMile - Mile marker (unused, kept for API compatibility)
     * @returns {number} Cost in wavedust
     */
    _calculatePackCost(quality, atMile) {
        // Use tier-based wavedust cost from balance config
        const tier = this.getWaveTier(quality);
        return LOT_OPEN_COST[tier] || LOT_OPEN_COST.faded;
    }

    /**
     * Roll for type-specific lot slot (for offline wave generation)
     * 25% of wave pickups are type-specific (slot-locked)
     * @returns {string|null} - Slot type ('rearview', 'dashboard', 'keychain') or null for generic
     * @private
     */
    _rollSlotType() {
        // Roll for type-specific (25% chance)
        if (Math.random() < TYPE_SPECIFIC_LOT.RATE) {
            // Randomly select one of the slot types
            const slots = TYPE_SPECIFIC_LOT.SLOT_TYPES;
            return slots[Math.floor(Math.random() * slots.length)];
        }
        return null;  // Generic lot
    }
}
